import {
  Avatar,
  Badge,
  ChannelIcon,
  CircleCheckBigIcon,
  EllipsisIcon,
  Flex,
  IconButton,
  Skeleton,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { isChannelIconService } from '../../util/isChannelIconService'
import { getCounterpart } from '../ConversationCard/conversationDisplay'
import type { ConversationCardData } from '../fragments/conversationCard'

import styles from './ConversationHeader.module.css'

import type { JSX } from 'react'

type ConversationHeaderProps = {
  /**
   * The open conversation, or `undefined` while the list that holds it is
   * still loading (a deep link lands before the first page does).
   */
  conversation: ConversationCardData | undefined
  isDetailsOpen: boolean
  onToggleDetails: () => void
  onResolve: (conversationId: string) => void
  onUnresolve: (conversationId: string) => void
}

/**
 * Top of the thread pane, per the DMs Figma `_conversation header`: who the
 * conversation is with, on which network, and the conversation actions.
 *
 * Reads the conversation node the list already fetched rather than the
 * thread's messages, so the header matches the row that was clicked and
 * renders before a single message has arrived.
 *
 * The identity block is a button that shows or hides the details pane; it is
 * the only way to open it. The resolve icon resolves the conversation; once
 * resolved, a labelled "Resolved" chip sits in its place, as the prototype
 * does, and clicking it undoes the resolve. The menu icon is inert until the
 * menu has actions of its own. Starring is not part of the first release, so
 * no star renders.
 */
export function ConversationHeader({
  conversation,
  isDetailsOpen,
  onToggleDetails,
  onResolve,
  onUnresolve,
}: ConversationHeaderProps): JSX.Element {
  if (!conversation) {
    return <ConversationHeaderSkeleton />
  }

  const counterpart = getCounterpart(conversation.participants)
  const service = conversation.serviceType
  const isResolved = conversation.status === 'resolved'

  return (
    <header className={styles.header}>
      <button
        type="button"
        className={styles.identity}
        onClick={onToggleDetails}
        aria-expanded={isDetailsOpen}
      >
        <Avatar
          src={counterpart.avatar}
          alt=""
          fallback="letter"
          fallbackName={counterpart.fallbackName}
          size="medium"
          className={styles.avatar}
        />
        <Flex as="span" direction="column" gap="3xs" className={styles.text}>
          <Text
            size="lg"
            weight="medium"
            lineHeight="tight"
            className={styles.truncated}
          >
            {counterpart.name}
          </Text>
          {counterpart.username && (
            <Flex as="span" align="center" gap="2xs">
              {isChannelIconService(service) && (
                <ChannelIcon type={service} size="small" color="subtle" />
              )}
              <Text
                weight="medium"
                color="subtle"
                lineHeight="tight"
                className={styles.truncated}
              >
                {counterpart.username}
              </Text>
            </Flex>
          )}
        </Flex>
        <VisuallyHidden>
          {isDetailsOpen ? 'Hide details' : 'Show details'}
        </VisuallyHidden>
      </button>
      <Flex align="center" gap="xs" className={styles.actions}>
        {isResolved ? (
          <Tooltip content="Undo resolve">
            <button
              type="button"
              className={styles.undo}
              onClick={(): void => onUnresolve(conversation.id)}
            >
              <Badge variant="neutral" size="medium">
                <CircleCheckBigIcon />
                Resolved
              </Badge>
              <VisuallyHidden>(undo resolve)</VisuallyHidden>
            </button>
          </Tooltip>
        ) : (
          <IconButton
            label="Resolve"
            tooltip="Resolve"
            variant="tertiary"
            size="small"
            onClick={(): void => onResolve(conversation.id)}
          >
            <CircleCheckBigIcon />
          </IconButton>
        )}
        <span className={styles.action} aria-hidden>
          <EllipsisIcon size="small" />
        </span>
      </Flex>
    </header>
  )
}

/** Same height and rhythm as the header, so the thread does not jump. */
function ConversationHeaderSkeleton(): JSX.Element {
  return (
    <header className={styles.header} aria-busy>
      <Flex align="center" gap="xs" className={styles.identity}>
        <Skeleton width="48px" height="48px" rounded />
        <Flex direction="column" gap="xs">
          <Skeleton height="1rem" width="160px" rounded />
          <Skeleton height="0.875rem" width="100px" rounded />
        </Flex>
      </Flex>
    </header>
  )
}
